import React, { useState, useEffect } from 'react'
import SafeIcon from '../common/SafeIcon'
import { supabase } from '../lib/supabase.js'
import { useAuth } from '../contexts/AuthContext'
import { imageLibraryService } from '../lib/imageLibraryService.js'
import ImageLibraryModal from './ImageLibrary/ImageLibraryModal'
import * as FiIcons from 'react-icons/fi'

const { FiPlus, FiTrash2, FiUpload, FiX, FiChevronDown, FiChevronUp, FiImage, FiFolder } = FiIcons

// Character Image Preview Component
const CharacterImagePreview = ({ character, className }) => {
  const [imageUrl, setImageUrl] = useState(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (character.imageId) {
      setLoading(true)
      // Fetch the actual image URL from user_image_library
      const fetchImage = async () => {
        try {
          const { data: imageRecord, error } = await supabase
            .from('user_image_library')
            .select('file_url')
            .eq('id', character.imageId)
            .single()

          if (!error && imageRecord?.file_url) {
            setImageUrl(imageRecord.file_url)
          }
        } catch (error) {
          console.error('Error fetching character image:', error)
        } finally {
          setLoading(false)
        }
      }
      fetchImage()
    }
  }, [character.imageId])

  if (!character.imageId) {
    return (
      <div className={`${className} bg-gray-100 flex items-center justify-center`}>
        <SafeIcon icon={FiImage} className="w-6 h-6 text-gray-400" />
      </div>
    )
  }

  if (loading) {
    return (
      <div className={`${className} bg-gray-200 flex items-center justify-center`}>
        <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    )
  }

  return (
    <div className={className} style={{ backgroundImage: `url(${imageUrl})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
      {!imageUrl && (
        <div className="w-full h-full bg-gray-200 flex items-center justify-center">
          <span className="text-xs text-gray-500">Photo</span>
        </div>
      )}
    </div>
  )
}


const AdvancedForm = ({ onDataChange, initialData = {} }) => {
  const { user } = useAuth()
  const [isExpanded, setIsExpanded] = useState(false)
  const [formData, setFormData] = useState({
    creator: initialData.creator || '',
    creatorCompany: initialData.creatorCompany || '',
    outline: initialData.outline || '',
    projectType: initialData.projectType || '',
    yearSet: initialData.yearSet || '',
    genre: initialData.genre || '',
    locations: initialData.locations || [],
    characters: initialData.characters || [],
    extraNotes: initialData.extraNotes || '',
    referenceImages: initialData.referenceImages || []
  })

  const [newLocation, setNewLocation] = useState('')
  const [newCharacter, setNewCharacter] = useState({
    name: '',
    role: '',
    bio: '',
    imageFile: null,
    imagePreview: null
  })
  const [referenceImageFiles, setReferenceImageFiles] = useState([])
  const [uploading, setUploading] = useState(false)
  
  // Image Library Modal states
  const [showCharacterLibrary, setShowCharacterLibrary] = useState(false)
  const [showBackgroundLibrary, setShowBackgroundLibrary] = useState(false)

  // Autocomplete suggestions
  const projectTypes = [
    'Feature Film', 'Limited Series', 'TV Series', 'Mini-Series',
    'Short Film', 'Documentary', 'Animated Feature', 'Animated Series',
    'Web Series', 'Micro-Drama Series', 'Pilot Episode', 'TV Movie'
  ]

  const genres = [
    'Action', 'Adventure', 'Comedy', 'Drama', 'Horror', 'Thriller',
    'Romance', 'Sci-Fi', 'Fantasy', 'Mystery', 'Crime', 'Western',
    'Animation', 'Documentary', 'Musical', 'Biography', 'War', 'History'
  ]

  const locations = [
    'New York City', 'Los Angeles', 'London', 'Paris', 'Tokyo', 'Sydney',
    'Chicago', 'Miami', 'San Francisco', 'Vancouver', 'Toronto', 'Berlin',
    'Rome', 'Barcelona', 'Amsterdam', 'Dublin', 'Vienna', 'Prague',
    'Budapest', 'Warsaw', 'Moscow', 'St. Petersburg', 'Beijing', 'Shanghai',
    'Hong Kong', 'Singapore', 'Bangkok', 'Dubai', 'Istanbul', 'Cairo'
  ]

  useEffect(() => {
    onDataChange(formData)
  }, [formData, onDataChange])

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const addLocation = () => {
    if (newLocation.trim() && !formData.locations.find(loc => loc.name === newLocation.trim())) {
      setFormData(prev => ({
        ...prev,
        locations: [...prev.locations, { name: newLocation.trim(), description: '' }]
      }))
      setNewLocation('')
    }
  }

  const removeLocation = (index) => {
    setFormData(prev => ({
      ...prev,
      locations: prev.locations.filter((_, i) => i !== index)
    }))
  }

  const updateLocation = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      locations: prev.locations.map((loc, i) =>
        i === index ? { ...loc, [field]: value } : loc
      )
    }))
  }

  const addCharacter = async () => {
    if (newCharacter.name.trim() && newCharacter.role.trim()) {
      let characterImageId = null

      // Use gallery image ID if available, otherwise upload new file
      if (newCharacter.imageId) {
        // Using existing gallery image
        characterImageId = newCharacter.imageId
      } else if (newCharacter.imageFile) {
        // Upload new character image
        setUploading(true)
        try {
          const imageId = await uploadImageToLibrary(newCharacter.imageFile, 'character', newCharacter.name)
          characterImageId = imageId
        } catch (error) {
          console.error('Error uploading character image:', error)
        } finally {
          setUploading(false)
        }
      }

      setFormData(prev => ({
        ...prev,
        characters: [...prev.characters, {
          name: newCharacter.name.trim(),
          role: newCharacter.role.trim(),
          bio: newCharacter.bio.trim(),
          imageId: characterImageId
        }]
      }))

      setNewCharacter({
        name: '',
        role: '',
        bio: '',
        imageFile: null,
        imagePreview: null,
        imageId: null
      })
    }
  }

  const removeCharacter = (index) => {
    setFormData(prev => ({
      ...prev,
      characters: prev.characters.filter((_, i) => i !== index)
    }))
  }

  const handleCharacterImageSelect = (e) => {
    const file = e.target.files[0]
    if (file) {
      setNewCharacter(prev => ({
        ...prev,
        imageFile: file,
        imagePreview: URL.createObjectURL(file)
      }))
    }
  }

  // Handle selecting character image from gallery
  const handleCharacterImageFromGallery = (imageData) => {
    setNewCharacter(prev => ({
      ...prev,
      imageFile: null, // Clear file since we're using existing image
      imagePreview: imageData.file_url || imageData.url,
      imageId: imageData.id // Store the gallery image ID
    }))
  }

  // Handle selecting background images from gallery
  const handleBackgroundImageFromGallery = (imageData) => {
    setFormData(prev => ({
      ...prev,
      referenceImages: [...prev.referenceImages, imageData.id]
    }))
    setReferenceImageFiles(prev => [...prev, { 
      name: imageData.file_name || imageData.alt_text || 'Gallery Image',
      url: imageData.file_url || imageData.url 
    }])
  }

  const uploadImageToLibrary = async (file, sourceType, description = '') => {
    try {
      console.log(`📤 Uploading ${sourceType} image to library:`, file.name)
      
      // Use the existing imageLibraryService to properly handle uploads and quotas
      const imageRecord = await imageLibraryService.uploadImage(user.id, file, {
        sourceType: sourceType,
        description: description,
        altText: description,
        tags: sourceType === 'character' ? ['character', 'advanced-form'] : ['background', 'advanced-form']
      })

      console.log(`✅ Successfully uploaded ${sourceType} image:`, imageRecord.id)
      return imageRecord.id
    } catch (error) {
      console.error('Error uploading image to library:', error)
      throw error
    }
  }

  const handleReferenceImageSelect = async (e) => {
    const files = Array.from(e.target.files)
    setUploading(true)

    try {
      const uploadedImageIds = []
      const uploadedFiles = []
      let successCount = 0
      let quotaExceeded = false

      for (const file of files) {
        try {
          const imageId = await uploadImageToLibrary(file, 'background', 'Background image for project')
          uploadedImageIds.push(imageId)
          uploadedFiles.push(file)
          successCount++
          console.log(`✅ Successfully uploaded ${file.name}`)
        } catch (uploadError) {
          if (uploadError.message.includes('Storage quota exceeded')) {
            quotaExceeded = true
            console.warn(`⚠️ Storage quota exceeded while uploading ${file.name}`)
            break // Stop uploading more files
          } else {
            console.error(`❌ Failed to upload ${file.name}:`, uploadError)
          }
        }
      }

      // Update state with successfully uploaded images
      if (uploadedImageIds.length > 0) {
        setFormData(prev => ({
          ...prev,
          referenceImages: [...prev.referenceImages, ...uploadedImageIds]
        }))
        setReferenceImageFiles(prev => [...prev, ...uploadedFiles])
      }

      // Show user-friendly messages
      if (quotaExceeded) {
        if (successCount > 0) {
          alert(`✅ Successfully uploaded ${successCount} image(s).\n\n⚠️ Storage quota exceeded - couldn't upload remaining images.\n\nTo upload more images:\n• Go to your Image Library and delete some old AI-generated images\n• Or contact support to upgrade your storage quota`)
        } else {
          alert(`⚠️ Storage quota exceeded!\n\nYou've reached your 50MB storage limit.\n\nTo upload images:\n• Go to your Image Library and delete some old AI-generated images\n• Or contact support to upgrade your storage quota`)
        }
      } else if (successCount === files.length) {
        console.log(`🎉 All ${successCount} background images uploaded successfully!`)
      } else if (successCount > 0) {
        alert(`⚠️ Uploaded ${successCount} of ${files.length} images.\n\nSome uploads failed - check console for details.`)
      }

    } catch (error) {
      console.error('Error uploading background images:', error)
      alert(`❌ Upload failed: ${error.message}`)
    } finally {
      setUploading(false)
    }
  }

  const removeReferenceImage = (index) => {
    setFormData(prev => ({
      ...prev,
      referenceImages: prev.referenceImages.filter((_, i) => i !== index)
    }))
    setReferenceImageFiles(prev => prev.filter((_, i) => i !== index))
  }

  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm mb-8">
      <div
        className="p-6 cursor-pointer flex items-center justify-between"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Advanced Options</h3>
          <p className="text-sm text-gray-600 mt-1">
            Add detailed project information, characters, and background images
          </p>
        </div>
        <SafeIcon
          icon={isExpanded ? FiChevronUp : FiChevronDown}
          className="w-5 h-5 text-gray-400"
        />
      </div>

      {isExpanded && (
        <div className="px-6 pb-6 border-t border-gray-100">
          <div className="space-y-6">
            {/* Creator Information */}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Creator Name
                </label>
                <input
                  type="text"
                  value={formData.creator}
                  onChange={(e) => handleInputChange('creator', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="e.g., John Smith"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Company/Studio
                </label>
                <input
                  type="text"
                  value={formData.creatorCompany}
                  onChange={(e) => handleInputChange('creatorCompany', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="e.g., Indie Films Co."
                />
              </div>
            </div>

            {/* Project Outline */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Project Outline
              </label>
              <textarea
                value={formData.outline}
                onChange={(e) => handleInputChange('outline', e.target.value)}
                className="w-full h-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                placeholder="Provide a detailed description of your project..."
              />
            </div>

            {/* Project Type, Year, Genre */}
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Project Type
                </label>
                <input
                  type="text"
                  value={formData.projectType}
                  onChange={(e) => handleInputChange('projectType', e.target.value)}
                  list="projectTypes"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="e.g., Feature Film"
                />
                <datalist id="projectTypes">
                  {projectTypes.map(type => (
                    <option key={type} value={type} />
                  ))}
                </datalist>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Year Set
                </label>
                <input
                  type="number"
                  value={formData.yearSet}
                  onChange={(e) => handleInputChange('yearSet', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="e.g., 2024"
                  min="1900"
                  max="2100"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Genre
                </label>
                <input
                  type="text"
                  value={formData.genre}
                  onChange={(e) => handleInputChange('genre', e.target.value)}
                  list="genres"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="e.g., Horror"
                />
                <datalist id="genres">
                  {genres.map(genre => (
                    <option key={genre} value={genre} />
                  ))}
                </datalist>
              </div>
            </div>

            {/* Locations */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Locations
              </label>
              <div className="flex gap-2 mb-3">
                <input
                  type="text"
                  value={newLocation}
                  onChange={(e) => setNewLocation(e.target.value)}
                  list="locations"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Add a location..."
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addLocation())}
                />
                <button
                  onClick={addLocation}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                >
                  <SafeIcon icon={FiPlus} className="w-4 h-4" />
                </button>
              </div>
              <datalist id="locations">
                {locations.map(location => (
                  <option key={location} value={location} />
                ))}
              </datalist>
              {formData.locations.length > 0 && (
                <div className="space-y-2">
                  {formData.locations.map((location, index) => (
                    <div key={index} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                      <span className="flex-1 font-medium">{location.name}</span>
                      <input
                        type="text"
                        value={location.description}
                        onChange={(e) => updateLocation(index, 'description', e.target.value)}
                        placeholder="Optional description..."
                        className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-primary-500 focus:border-transparent"
                      />
                      <button
                        onClick={() => removeLocation(index)}
                        className="p-1 text-red-500 hover:text-red-700"
                      >
                        <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Characters */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Characters
              </label>
              <div className="space-y-3 mb-4">
                <div className="grid md:grid-cols-2 gap-3">
                  <input
                    type="text"
                    value={newCharacter.name}
                    onChange={(e) => setNewCharacter(prev => ({ ...prev, name: e.target.value }))}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Character name"
                  />
                  <input
                    type="text"
                    value={newCharacter.role}
                    onChange={(e) => setNewCharacter(prev => ({ ...prev, role: e.target.value }))}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Character role"
                  />
                </div>
                <textarea
                  value={newCharacter.bio}
                  onChange={(e) => setNewCharacter(prev => ({ ...prev, bio: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                  placeholder="Character bio/description..."
                  rows="2"
                />
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <label className="flex-1">
                      <input
                        type="file"
                        onChange={handleCharacterImageSelect}
                        accept="image/*"
                        className="hidden"
                      />
                      <div className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg cursor-pointer hover:border-primary-500 transition-colors">
                        <SafeIcon icon={FiUpload} className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-600">
                          {newCharacter.imageFile ? newCharacter.imageFile.name : 'Upload character photo'}
                        </span>
                      </div>
                    </label>
                    <button
                      onClick={() => setShowCharacterLibrary(true)}
                      className="px-3 py-2 border border-gray-300 rounded-lg hover:border-primary-500 transition-colors"
                      title="Select from gallery"
                    >
                      <SafeIcon icon={FiFolder} className="w-4 h-4 text-gray-400" />
                    </button>
                  </div>
                  <div className="flex justify-end">
                    <button
                      onClick={addCharacter}
                      disabled={!newCharacter.name.trim() || !newCharacter.role.trim() || uploading}
                      className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {uploading ? 'Uploading...' : 'Add Character'}
                    </button>
                  </div>
                </div>
                {newCharacter.imagePreview && (
                  <div className="relative inline-block">
                    <img
                      src={newCharacter.imagePreview}
                      alt="Character preview"
                      className="w-16 h-16 object-cover rounded-lg border"
                    />
                    <button
                      onClick={() => setNewCharacter(prev => ({ ...prev, imageFile: null, imagePreview: null, imageId: null }))}
                      className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
                    >
                      <SafeIcon icon={FiX} className="w-3 h-3" />
                    </button>
                  </div>
                )}
              </div>
              {formData.characters.length > 0 && (
                <div className="space-y-3">
                  {formData.characters.map((character, index) => (
                    <div key={index} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                      <CharacterImagePreview 
                        character={character}
                        className="w-12 h-12 bg-gray-200 rounded-lg flex-shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium">{character.name}</div>
                        <div className="text-sm text-gray-600">{character.role}</div>
                        <div className="text-sm text-gray-500 break-words whitespace-pre-wrap">{character.bio}</div>
                      </div>
                      <button
                        onClick={() => removeCharacter(index)}
                        className="p-1 text-red-500 hover:text-red-700"
                      >
                        <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Background Images */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Background Images
              </label>
              <div className="space-y-3">
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handleReferenceImageSelect}
                    className="hidden"
                    id="reference-images"
                  />
                  <label htmlFor="reference-images" className="cursor-pointer">
                    <div className="text-center">
                      <SafeIcon icon={FiUpload} className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                      <p className="text-sm text-gray-600">
                        {uploading ? 'Uploading images...' : 'Click to upload background images'}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        These images will be saved to your library and used as slide backgrounds
                      </p>
                    </div>
                  </label>
                </div>
                <div className="text-center">
                  <button
                    onClick={() => setShowBackgroundLibrary(true)}
                    className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:border-primary-500 transition-colors"
                  >
                    <SafeIcon icon={FiFolder} className="w-4 h-4 text-gray-400" />
                    <span className="text-sm text-gray-600">Select from Gallery</span>
                  </button>
                </div>
              </div>
              {referenceImageFiles.length > 0 && (
                <div className="mt-3 grid grid-cols-4 gap-2">
                  {referenceImageFiles.map((file, index) => (
                    <div key={index} className="relative">
                      <img
                        src={file.url || URL.createObjectURL(file)}
                        alt={`Background ${index + 1}`}
                        className="w-full h-16 object-cover rounded-lg border"
                      />
                      <button
                        onClick={() => removeReferenceImage(index)}
                        className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
                      >
                        <SafeIcon icon={FiX} className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Extra Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Extra Notes
              </label>
              <textarea
                value={formData.extraNotes}
                onChange={(e) => handleInputChange('extraNotes', e.target.value)}
                className="w-full h-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                placeholder="Any additional information about your project..."
              />
            </div>
          </div>
        </div>
      )}

      {/* Image Library Modals */}
      <ImageLibraryModal
        isOpen={showCharacterLibrary}
        onClose={() => setShowCharacterLibrary(false)}
        onImageSelected={handleCharacterImageFromGallery}
        projectTitle="Advanced Form"
        elementType="character"
      />
      
      <ImageLibraryModal
        isOpen={showBackgroundLibrary}
        onClose={() => setShowBackgroundLibrary(false)}
        onImageSelected={handleBackgroundImageFromGallery}
        projectTitle="Advanced Form"
        elementType="background"
      />
    </div>
  )
}

export default AdvancedForm
